'use client'

import { ATTRIBUTION_QUERY_PARAMS, IAttributionQueryParams } from '@/features/attribution/utils/constants'
import { LOCAL_STORAGE_KEYS } from '@/setup/local-storage'
import { useQueryParams } from '@/utils/url/use-query-params'
import { useEffect, useState } from 'react'

interface IDubStoredParams {
    clickId: string | null
}

/**
 * Hook to get the Dub affiliate click id from browser storage or the URL.
 */
export function useAttributionParams(): IDubStoredParams {
    const { getParam } = useQueryParams<IAttributionQueryParams>()
    const [attributionParams, setAttributionParams] = useState<IDubStoredParams>({ clickId: null })

    useEffect(() => {
        if (typeof window === 'undefined') return

        const stored = localStorage.getItem(LOCAL_STORAGE_KEYS.Attribution.Dub)
        if (stored) {
            setAttributionParams(JSON.parse(stored) as IDubStoredParams)
            return
        }

        let clickId = getParam(ATTRIBUTION_QUERY_PARAMS.DubId) || null

        if (!clickId) {
            const redirectUrl = getParam(ATTRIBUTION_QUERY_PARAMS.RedirectUrl)
            if (redirectUrl) {
                try {
                    // `redirect_url` may be a relative in-app path (e.g. /integrations/klaviyo/install),
                    // not an absolute URL — resolve it against the current origin so `new URL` doesn't
                    // throw on a relative value and crash RootLayout. We only read its query params.
                    const decodedUrl = decodeURIComponent(redirectUrl)
                    const url = new URL(decodedUrl, window.location.origin)
                    clickId = url.searchParams.get(ATTRIBUTION_QUERY_PARAMS.DubId) || null
                } catch {
                    // Malformed redirect_url — skip attribution rather than taking down the app.
                }
            }
        }

        if (clickId) {
            const params: IDubStoredParams = { clickId }
            localStorage.setItem(LOCAL_STORAGE_KEYS.Attribution.Dub, JSON.stringify(params))
            setAttributionParams(params)
        }
    }, [getParam])

    return attributionParams
}

/**
 * Clear stored attribution parameters from localStorage
 */
export function clearStoredAttributionParams() {
    if (typeof window === 'undefined') return
    localStorage.removeItem(LOCAL_STORAGE_KEYS.Attribution.Dub)
}

/**
 * Clean URL by removing attribution parameters
 */
export function cleanAttributionURL() {
    if (typeof window === 'undefined') return

    const url = new URL(window.location.href)
    url.searchParams.delete(ATTRIBUTION_QUERY_PARAMS.DubId)

    window.history.replaceState({}, document.title, url.toString())
}
